import React, { useEffect, useState } from 'react';
import Layout from '../components/shared/Layout';
import { leaveAPI, holidayAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { useConfirm } from '../components/shared/ConfirmDialog';
import { ClipboardList, Plus, X, CheckCircle2, XCircle, Clock, AlertTriangle, Info, Sun, Sunset } from 'lucide-react';

const statusBadge = (s) => {
  const map = {
    pending:   { label: 'Pendiente', cls: 'badge-pending',  Icon: Clock },
    approved:  { label: 'Aprobado',  cls: 'badge-approved', Icon: CheckCircle2 },
    rejected:  { label: 'Rechazado', cls: 'badge-rejected', Icon: XCircle },
    cancelled: { label: 'Cancelado', cls: 'badge-cancelled', Icon: X },
  };
  const { label, cls, Icon } = map[s] || map.pending;
  return (
    <span className={`badge ${cls}`} style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
      <Icon size={11} strokeWidth={2} /> {label}
    </span>
  );
};

const durationLabel = (r) => {
  if (parseFloat(r.days) === 1)          return 'Día completo';
  if (r.half_day_period === 'morning')   return 'Medio día (mañana)';
  if (r.half_day_period === 'afternoon') return 'Medio día (tarde)';
  return `${r.days}d`;
};

function LimitBar({ label, used, limit, color }) {
  const pct = Math.min((used / limit) * 100, 100);
  const remaining = parseFloat((limit - used).toFixed(1));
  const isFull = pct >= 100;
  const isWarn = pct >= 80;
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
        <span style={{ fontSize:13, fontWeight:500, color:'var(--gray-700)' }}>{label}</span>
        <span style={{ fontSize:13 }}>
          <span style={{ color:'var(--gray-500)' }}>{used.toFixed(1)} / {limit}d usados</span>
          {remaining > 0
            ? <span style={{ marginLeft:8, color: color || 'var(--primary)', fontWeight:600 }}>{remaining}d disponibles</span>
            : <span style={{ marginLeft:8, color:'var(--danger)', fontWeight:600 }}>Límite alcanzado</span>
          }
        </span>
      </div>
      <div className="days-bar-track" style={{ height:8 }}>
        <div style={{
          height:'100%', borderRadius:4, transition:'width 0.4s', width:`${pct}%`,
          background: isFull ? 'var(--danger)' : isWarn ? 'var(--warning)' : color || 'var(--primary)',
        }} />
      </div>
    </div>
  );
}

export default function LeavesPage() {
  const confirm = useConfirm();
  const [requests,  setRequests]  = useState([]);
  const [dateHoliday, setDateHoliday] = useState(null); // feriado en la fecha seleccionada
  const [summary,   setSummary]   = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ request_date:'', days:'', half_day_period:'', reason:'' });
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    const [lRes, sRes] = await Promise.all([
      leaveAPI.getMine().catch(() => ({ data:[] })),
      leaveAPI.getSummary().catch(() => ({ data:null })),
    ]);
    setRequests(lRes.data);
    setSummary(sRes.data);
  };

  // Verificar si la fecha seleccionada es feriado
  useEffect(() => {
    if (form.request_date) {
      holidayAPI.checkRange({ start_date: form.request_date, end_date: form.request_date })
        .then(r => setDateHoliday(r.data.holidays?.[0] || null))
        .catch(() => setDateHoliday(null));
    } else {
      setDateHoliday(null);
    }
  }, [form.request_date]);

  const handleCancel = async (id) => {
    const ok = await confirm({
      title: '¿Cancelar este permiso?',
      message: 'La solicitud quedará cancelada y ya no será revisada.',
      variant: 'warning',
      confirmLabel: 'Sí, cancelar',
    });
    if (!ok) return;
    try {
      await leaveAPI.cancel(id);
      toast.success('Permiso cancelado');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al cancelar');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await leaveAPI.create(form);
      toast.success('Permiso solicitado correctamente');
      setShowModal(false);
      setForm({ request_date:'', days:'', half_day_period:'', reason:'' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al solicitar permiso');
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toLocaleDateString('en-CA');
  const isHalfDay = form.days === '0.5';
  const monthNames = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const currentMonth = summary ? monthNames[summary.month - 1] : '';

  const formValid = !!(form.request_date && form.days && (form.days === '1' || form.half_day_period) && !dateHoliday);

  return (
    <Layout title="Mis permisos">
      <div className="page-header">
        <div>
          <div className="page-title">Mis permisos</div>
          <div className="page-subtitle">Solicita permisos de día libre</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}
          style={{ display:'flex', alignItems:'center', gap:6 }}>
          <Plus size={16} /> Solicitar permiso
        </button>
      </div>

      {/* Límites */}
      {summary && (
        <div className="card section-gap">
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:16 }}>
            <Info size={15} color="var(--primary)" />
            <span style={{ fontSize:13, fontWeight:600, color:'var(--gray-700)' }}>Límites de permisos</span>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <LimitBar label={`Anual ${summary.year}`} used={summary.annual.used} limit={3} />
            <LimitBar label={`Mensual — ${currentMonth} ${summary.year}`} used={summary.monthly.used} limit={1} color="var(--success)" />
          </div>
          <div style={{ marginTop:16, padding:'10px 14px', background:'var(--gray-50)', borderRadius:8, fontSize:12, color:'var(--gray-500)' }}>
            <div style={{ fontWeight:600, color:'var(--gray-700)', marginBottom:6 }}>Reglas</div>
            <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
              <div>• Máximo <strong>3 días por año</strong> (los medios días cuentan como 0.5)</div>
              <div>• Máximo <strong>1 día por mes</strong> — un día completo o dos medio día</div>
              <div>• El medio día puede ser <strong>mañana</strong> o <strong>tarde</strong>, no ambos el mismo día</div>
              <div>• No se puede solicitar en fechas con vacaciones pendientes o aprobadas</div>
            </div>
          </div>
          {summary.annual.remaining <= 0 && (
            <div className="alert alert-error" style={{ marginTop:12, display:'flex', alignItems:'center', gap:8 }}>
              <AlertTriangle size={15} /> Alcanzaste el límite anual de 3 días para {summary.year}
            </div>
          )}
        </div>
      )}

      {/* Historial */}
      <div className="card">
        <div className="card-header"><div className="card-title">Historial de permisos de día libre</div></div>
        {requests.length === 0 ? (
          <div className="empty-state">
            <ClipboardList size={40} color="var(--gray-300)" style={{ margin:'0 auto 12px', display:'block' }} />
            <div className="empty-state-title">Sin permisos solicitados</div>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>Fecha</th><th>Duración / Turno</th><th>Motivo</th><th>Estado</th><th>Solicitud</th><th></th></tr>
              </thead>
              <tbody>
                {requests.map(r => (
                  <tr key={r.id}>
                    <td style={{ whiteSpace:'nowrap' }}>
                      {r.request_date.slice(0, 10).split('-').reverse().join('/')}
                    </td>
                    <td><strong>{durationLabel(r)}</strong></td>
                    <td style={{ maxWidth:220 }} className="truncate">{r.reason || '—'}</td>
                    <td>{statusBadge(r.status)}</td>
                    <td style={{ color:'var(--gray-500)', fontSize:13 }}>{format(new Date(r.created_at), 'dd/MM/yyyy')}</td>
                    <td>
                      {r.status === 'pending' && (
                        <button className="btn btn-secondary btn-sm"
                          onClick={() => handleCancel(r.id)}
                          style={{ display:'flex', alignItems:'center', gap:4 }}>
                          <X size={13} /> Cancelar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title" style={{ display:'flex', alignItems:'center', gap:8 }}>
                <ClipboardList size={18} color="var(--primary)" /> Solicitar permiso
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">

                {/* Resumen disponible */}
                {summary && (
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
                    <div style={{ padding:'10px 14px', background:'var(--primary-light)', borderRadius:8 }}>
                      <div style={{ fontSize:11, color:'var(--gray-500)', marginBottom:2 }}>Disponible este año</div>
                      <div style={{ fontWeight:700, fontSize:20, color:'var(--primary)' }}>{summary.annual.remaining}d</div>
                      <div style={{ fontSize:11, color:'var(--gray-400)' }}>de 3 días anuales</div>
                    </div>
                    <div style={{ padding:'10px 14px', background:'var(--success-light)', borderRadius:8 }}>
                      <div style={{ fontSize:11, color:'var(--gray-500)', marginBottom:2 }}>Disponible este mes</div>
                      <div style={{ fontWeight:700, fontSize:20, color:'var(--success)' }}>{summary.monthly.remaining}d</div>
                      <div style={{ fontSize:11, color:'var(--gray-400)' }}>de 1 día mensual</div>
                    </div>
                  </div>
                )}

                {/* Fecha */}
                <div className="form-group">
                  <label className="form-label">Fecha *</label>
                    <input type="date" className="form-input" value={form.request_date} min={today} required
                      onChange={e => setForm(f => ({ ...f, request_date: e.target.value }))} />
                </div>

                {/* Aviso de feriado */}
                {dateHoliday && (
                  <div style={{
                    padding: '10px 14px', borderRadius: 8, marginBottom: 4,
                    background: '#fffbeb', border: '1px solid #fcd34d',
                    fontSize: 13, color: '#92400e', fontWeight: 500,
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <AlertTriangle size={15} color='#d97706' />
                    No se puede solicitar permiso el {new Date(form.request_date + 'T00:00:00').toLocaleDateString('es', { day: '2-digit', month: 'long', year: 'numeric' })}: es feriado ({dateHoliday.name})
                  </div>
                )}

                {/* Duración */}
                <div className="form-group">
                  <label className="form-label">Duración *</label>
                  <div style={{ display:'flex', gap:10 }}>
                    {[{ val:'0.5', label:'Medio día' }, { val:'1', label:'Día completo' }].map(opt => (
                      <button key={opt.val} type="button"
                        onClick={() => setForm(f => ({ ...f, days: opt.val, half_day_period: opt.val === '1' ? '' : f.half_day_period }))}
                        style={{
                          flex:1, padding:'9px 12px', borderRadius:8, cursor:'pointer',
                          fontFamily:'var(--font)', fontSize:14, fontWeight:500,
                          border: form.days === opt.val ? '2px solid var(--primary)' : '2px solid var(--gray-200)',
                          background: form.days === opt.val ? 'var(--primary-light)' : 'white',
                          color: form.days === opt.val ? 'var(--primary)' : 'var(--gray-700)',
                          transition:'all 0.15s',
                        }}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Turno — solo para medio día */}
                {isHalfDay && (
                  <div className="form-group">
                    <label className="form-label">Turno *</label>
                    <div style={{ display:'flex', gap:10 }}>
                      {[{ val:'morning', label:'Mañana', Icon: Sun }, { val:'afternoon', label:'Tarde', Icon: Sunset }].map(({ val, label, Icon: Ic }) => (
                        <button key={val} type="button"
                          onClick={() => setForm(f => ({ ...f, half_day_period: val }))}
                          style={{
                            flex:1, padding:'10px 12px', borderRadius:8, cursor:'pointer',
                            fontFamily:'var(--font)', fontSize:14, fontWeight:500,
                            border: form.half_day_period === val ? '2px solid var(--primary)' : '2px solid var(--gray-200)',
                            background: form.half_day_period === val ? 'var(--primary-light)' : 'white',
                            color: form.half_day_period === val ? 'var(--primary)' : 'var(--gray-700)',
                            transition:'all 0.15s',
                            display:'flex', alignItems:'center', justifyContent:'center', gap:6,
                          }}>
                          <Ic size={16} strokeWidth={1.8} /> {label}
                        </button>
                      ))}
                    </div>
                    <div style={{ fontSize:12, color:'var(--gray-500)', marginTop:6, display:'flex', alignItems:'center', gap:4 }}>
                      <Info size={12} /> Solo un turno por día
                    </div>
                  </div>
                )}

                {/* Motivo */}
                <div className="form-group">
                  <label className="form-label">Motivo (opcional)</label>
                  <textarea className="form-input form-textarea"
                    placeholder="Describe el motivo del permiso..."
                    value={form.reason}
                    onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={loading || !formValid}>
                  {loading ? 'Enviando…' : 'Solicitar permiso'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
